import { useState, useEffect, useContext } from "react";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { useTasks } from "./Hooks/useTasks";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { getSiteUsers } from "../../Services/siteServices";
import { UserContext } from "../../Context";
import { TASK_ACTIONS, TASK_STATUS, TOAST_MESSAGES } from "./Constants";
import { ROLES } from "../../Constants/Strings";
import {
  ProjectSiteDetailsInfo,
  TaskDetailsInfo,
  TaskFiles,
  TaskComments,
  SubTaskDetailsInfo,
} from "./Components";

const getTaskActions = (userRole, taskStatus, isAssignedTo, isSuperTask) => {
  switch (taskStatus) {
    case TASK_STATUS.PENDING:
      if (
        [ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER].includes(userRole) &&
        isAssignedTo
      )
        return [TASK_ACTIONS.START];
      break;

    case TASK_STATUS.IN_PROGRESS:
      if (
        [ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER].includes(userRole) &&
        isAssignedTo
      )
        return [TASK_ACTIONS.SUBMIT, TASK_ACTIONS.MAKE_PENDING];
      break;

    case TASK_STATUS.PENDING_L1_APPROVAL:
      if (
        (isSuperTask && userRole === ROLES.PROJECT_MANAGER) ||
        (!isSuperTask && userRole === ROLES.PROJECT_ENGINEER)
      )
        return [TASK_ACTIONS.APPROVE, TASK_ACTIONS.REJECT];
      break;

    case TASK_STATUS.CLOSED:
    case TASK_STATUS.CANCELLED:
      return [];
    default:
      return [];
  }
  return [];
};

const TaskDetails = () => {
  const navigate = useNavigate();
  const { instanceId } = useParams();
  const { user } = useContext(UserContext);

  const [task, setTask] = useState(null);
  const [formData, setFormData] = useState({});
  const [siteUser, setSiteUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [isAssignedTo, setIsAssignedTo] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const isSubTask = task?.super_task !== null;

  const { loading, fetchTaskById, taskActions, editTaskById } = useTasks({
    fetch: false,
  });

  const loadTask = async () => {
    const data = await fetchTaskById(instanceId);
    setTask(data);
    setIsAssignedTo(user.id === data?.assigned_to?.id);
    setFormData({
      description: data?.description || "",
      weight: data?.weight || "0",
      assigned_to: data?.assigned_to?.id || "",
      start_date: data?.start_date || null,
      estimated_end_date: data?.estimated_end_date || null,
      site_name: data?.site_name || null,
    });
  };

  useEffect(() => {
    loadTask();
  }, [instanceId]);

  useEffect(() => {
    if (editMode && task) fetchSiteUsers();
  }, [editMode]);

  //? DONE
  const fetchSiteUsers = async () => {
    setUserLoading(true);
    try {
      const res = await getSiteUsers(task.site, {
        limit: 0,
        role: task.super_task ? ROLES.SITE_ENGINEER : ROLES.PROJECT_ENGINEER,
      });
      if (res.status === 200)
        setSiteUsers(res?.data || res?.data?.results || []);
      else toast.error(TOAST_MESSAGES.USER_FETCH_FAILED);
    } catch (err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.USER_FETCH_FAILED;
      toast.error(msg);
    } finally {
      setUserLoading(false);
    }
  };

  const handleChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    const data = await editTaskById(task.id, formData);
    if (data) {
      setTask(data);
    }
    setEditMode(false);
  };

  const handleTaskAction = async (action) => {
    await taskActions(task.id, action);
    loadTask();
  };

  const actions = getTaskActions(
    user?.role,
    task?.status,
    isAssignedTo,
    task?.super_task === null
  );

  if (loading || !task)
    return (
      <div className="flex justify-center items-center h-[150px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
      </div>
    );

  return (
    <motion.div
      className="w-full min-h-screen mx-auto p-4 bg-white shadow rounded-lg"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
    >
      <div className="flex items-center gap-2 p-1">
        <button onClick={() => navigate(-1)}>
          <ArrowLeft size={25} className="text-primary" />
        </button>
        <h1 className="text-2xl font-bold">{isSubTask ? "Sub-task" : "Task"}</h1>
      </div>

      {actions.length > 0 && (
        <div className="flex gap-4 justify-end text-sm my-2">
          {actions.includes(TASK_ACTIONS.START) && (
            <button
              className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              onClick={() => handleTaskAction(TASK_ACTIONS.APPROVE)}
            >
              Start
            </button>
          )}
          {actions.includes(TASK_ACTIONS.SUBMIT) && (
            <button
              className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600"
              onClick={() => handleTaskAction(TASK_ACTIONS.APPROVE)}
            >
              Submit
            </button>
          )}
          {actions.includes(TASK_ACTIONS.MAKE_PENDING) && (
            <button
              className="px-3 py-1 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
              onClick={() => handleTaskAction(TASK_ACTIONS.REJECT)}
            >
              Mark Pending
            </button>
          )}
          {actions.includes(TASK_ACTIONS.APPROVE) && (
            <button
              className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600"
              onClick={() => handleTaskAction(TASK_ACTIONS.APPROVE)}
            >
              Approve
            </button>
          )}
          {actions.includes(TASK_ACTIONS.REJECT) && (
            <button
              className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600"
              onClick={() => handleTaskAction(TASK_ACTIONS.REJECT)}
            >
              Reject
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-10 gap-3 m-2 items-stretch h-full">
        <div className="flex flex-col md:col-span-7 row gap-3 h-full">
          <div className="bg-white shadow-md border border-gray-100 p-3 flex-shrink-0">
            <TaskDetailsInfo
              task={task}
              editMode={editMode}
              setEditMode={setEditMode}
              handleSave={handleSave}
              handleChange={handleChange}
              siteUser={siteUser}
              userLoading={userLoading}
              formData={formData}
            />
          </div>
          <div className={`bg-white shadow-md border border-gray-100 p-3 ${!isSubTask ? "flex-shrink-0" : "flex-1 overflow-hidden"} `}>
            <div className={`${!isSubTask ? "h-full min-h-[17vh] max-h-[25vh] overflow-y-auto" : ""}`}>
              <ProjectSiteDetailsInfo task={task} />
            </div>
            
          </div>

        {!isSubTask && (
          <div className="bg-white shadow-md border border-gray-100 p-3 mb-2 flex-1 overflow-hidden">
            <div className="h-full min-h-[20vh] max-h-[35vh] overflow-y-auto">
              <SubTaskDetailsInfo subTasks={task?.sub_tasks || []} />
            </div>
          </div>
        )}
          
        </div>

        <div className="flex flex-col gap-3 md:col-span-3 h-full">
          <div className="bg-white shadow-md border border-gray-100 p-3 flex-1 overflow-hidden">
            <div className="h-full min-h-[35vh] max-h-[50vh] overflow-y-auto">
              <TaskFiles />
            </div>
          </div>
          <div className={`bg-white shadow-md border border-gray-100 p-3 ${!isSubTask ? "mb-2" : ""} flex-1 overflow-hidden`}>
            <div className="h-full min-h-[35vh] max-h-[50vh] overflow-y-auto">
              <TaskComments />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TaskDetails;
